import type { CSSProperties, FC } from "react";
import React, { useEffect, useRef, useState } from "react";

import cn from "classnames";

import type { IOverlay } from "../../../types/common";

import classes from "./Overlay.styl";

const OVERLAY_TEST_ID = "overlay-test-id";
const OVERLAY_OFFSET_X_VAR_NAME = "--overlay-offset-x";
const OVERLAY_OFFSET_Y_VAR_NAME = "--overlay-offset-y";
const OVERLAY_SCALE_FACTOR_VAR_NAME = "--scale-factor";

enum OverlayStates {
    MOUNT = "mount",
    SHOW = "show",
    SHOW_COMPLETE = "show_complete",
    SHOW_END = "show_end",
    SHOW_END_COMPLETE = "show_end_complete",
}

export interface IOverlayProps extends IOverlay {
    removeOverlay: () => void;
    finishAnimation: boolean;
    animationShowDelay?: number;
    animationShowEndDelay?: number;
}

export const Overlay: FC<IOverlayProps> = ({
    posX,
    posY,
    scaleFactor,
    removeOverlay,
    finishAnimation,
    animationShowDelay = 1000,
    animationShowEndDelay = 50000,
}) => {
    const [state, setState] = useState<OverlayStates>(OverlayStates.MOUNT);
    const showTimer = useRef<ReturnType<typeof setTimeout>>();
    const showEndTimer = useRef<ReturnType<typeof setTimeout>>();

    const overlayClasses = cn(classes["overlay"], {
        [classes[`__state-${state}` as keyof typeof classes]]: state,
    });

    /**
     * Анимация появления
     */
    useEffect(() => {
        setState(OverlayStates.SHOW);

        showTimer.current = setTimeout(() => {
            setState(OverlayStates.SHOW_COMPLETE);
        }, animationShowDelay);

        /**
         * Очистка таймеров при анмаунте
         */
        return () => {
            clearTimeout(showTimer.current);
            clearTimeout(showEndTimer.current);
        };
    }, [animationShowDelay]);

    /**
     * Анимация завершения
     */
    useEffect(() => {
        if (finishAnimation && state === OverlayStates.SHOW_COMPLETE) {
            setState(OverlayStates.SHOW_END);
            showEndTimer.current = setTimeout(() => {
                setState(OverlayStates.SHOW_END_COMPLETE);
            }, animationShowEndDelay);
        }
    }, [finishAnimation, state, animationShowEndDelay]);

    /**
     * Уничтожение компонента
     */
    useEffect(() => {
        if (state === OverlayStates.SHOW_END_COMPLETE) {
            removeOverlay();
        }
    }, [removeOverlay, state]);

    return (
        <div
            className={overlayClasses}
            style={
                {
                    [OVERLAY_OFFSET_X_VAR_NAME]: `${posX}px`,
                    [OVERLAY_OFFSET_Y_VAR_NAME]: `${posY}px`,
                    [OVERLAY_SCALE_FACTOR_VAR_NAME]: scaleFactor,
                } as CSSProperties
            }
            data-tsid={OVERLAY_TEST_ID}
        ></div>
    );
};
